import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { toast } from "react-toastify";

import Header from "../../../components/Header";
import {
  DURATION_OPTIONS,
  ELIGIBILITY_ROWS,
  PROMOTION_TYPE_OPTIONS,
  createPromotion,
  errorMessage,
  getPromotion,
  updatePromotion,
} from "./promotionApi";
import { Section, usePromotionStyles } from "./promotionStyles";

const EMPTY = {
  name: "",
  code: "",
  description: "",
  promotion_type: "PERCENTAGE",
  discount_value: "",
  valid_from: "",
  valid_until: "",
  max_redemptions: "",
  max_per_member: "1",
  new_members_only: false,
  terms: "",
  admin_notes: "",
  status: "DRAFT",
  eligibility: [],
};

const FIELD_LABELS = {
  name: "Name",
  code: "Code",
  promotion_type: "Promotion type",
  discount_value: "Value",
  valid_from: "Valid from",
  valid_until: "Valid until",
  max_redemptions: "Campaign limit",
  max_per_member: "Uses per member",
  eligibility: "Eligible packages",
  status: "Start as",
};

const sameRow = (a, b) =>
  a.package_category === b.package_category &&
  a.duration_code === b.duration_code &&
  (a.member_type || null) === (b.member_type || null);

const valueHelp = {
  PERCENTAGE: "Applied to the full monthly-rate value, not the package price.",
  FIXED: "Applied to the full monthly-rate value, not the package price.",
  SPECIAL_PRICE: "Applied to the full monthly-rate value, not the package price.",
  ADMISSION_WAIVER: "The admission fee becomes 0. No value needed.",
};

const HpcPromotionForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const styles = usePromotionStyles();
  const { colors } = styles;
  const [form, setForm] = useState(EMPTY);
  const [locked, setLocked] = useState([]);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    getPromotion(id)
      .then((data) => {
        const p = data.promotion;
        setForm({
          ...EMPTY,
          ...Object.fromEntries(
            Object.keys(EMPTY).map((key) => [key, p[key] === null || p[key] === undefined ? EMPTY[key] : p[key]]),
          ),
          discount_value: p.discount_value != null ? String(p.discount_value) : "",
          max_redemptions: p.max_redemptions != null ? String(p.max_redemptions) : "",
          max_per_member: String(p.max_per_member || 1),
          valid_from: (p.valid_from || "").slice(0, 10),
          valid_until: (p.valid_until || "").slice(0, 10),
          eligibility: p.eligibility || [],
        });
        setLocked(data.locked_fields || []);
      })
      .catch((err) => setLoadError(errorMessage(err, "Could not load the promotion")));
  }, [id, isEdit]);

  const isLocked = (field) => locked.includes(field);
  const set = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const toggleEligibility = (row, duration) => {
    const target = { package_category: row.package_category, member_type: row.member_type, duration_code: duration };
    setForm((current) => {
      const exists = current.eligibility.some((item) => sameRow(item, target));
      return {
        ...current,
        eligibility: exists
          ? current.eligibility.filter((item) => !sameRow(item, target))
          : [...current.eligibility, target],
      };
    });
    setErrors((current) => ({ ...current, eligibility: undefined }));
  };

  const submit = async () => {
    setSaving(true);
    setErrors({});
    setFormError(null);
    const payload = { ...form };
    if (isEdit) {
      delete payload.status;
      locked.forEach((field) => delete payload[field]);
    }
    try {
      if (isEdit) {
        await updatePromotion(id, payload);
        toast.success("Promotion updated");
        navigate(`/hpc/promotions/${id}`);
      } else {
        const result = await createPromotion(payload);
        toast.success("Promotion created");
        navigate(`/hpc/promotions/${result.id}`);
      }
    } catch (err) {
      setErrors(err.response?.data?.errors || {});
      setFormError(err.response?.data?.error || "Could not save the promotion.");
      toast.error(errorMessage(err, "Could not save the promotion"));
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Box m="20px">
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  const field = (name, label, { helperText, disabled, ...props } = {}) => (
    <TextField
      fullWidth
      label={label}
      value={form[name]}
      onChange={set(name)}
      error={Boolean(errors[name])}
      helperText={errors[name] || helperText}
      disabled={isLocked(name) || Boolean(disabled)}
      InputProps={isLocked(name) ? { endAdornment: <LockOutlinedIcon fontSize="small" sx={{ color: colors.grey[400] }} /> } : undefined}
      {...props}
    />
  );

  const errorEntries = Object.entries(errors).filter(([, message]) => message);
  const backTo = isEdit ? `/hpc/promotions/${id}` : "/hpc/promotions";

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }} sx={styles.pageSx}>
      <Header
        title={isEdit ? "Edit promotion" : "New promotion"}
        subtitle="Promotion codes apply once to the main member's first registration payment"
      />

      {locked.length > 0 && (
        <Alert severity="info" icon={<LockOutlinedIcon />} sx={{ mb: 2 }}>
          This promotion has been used. The code, type, value, start date, new-member rule and eligibility are
          locked. Name, description, end date, limits, terms and notes can still change.
        </Alert>
      )}
      {formError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={700}>{formError}</Typography>
          {errorEntries.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {errorEntries.map(([key, message]) => (
                <li key={key}>
                  <Typography variant="body2">{FIELD_LABELS[key] || key}: {message}</Typography>
                </li>
              ))}
            </Box>
          )}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Section title="Basic info" subtitle="What staff see in the promotion list" colors={colors}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>{field("name", "Name", { required: true })}</Grid>
              <Grid item xs={12} sm={6}>
                {field("code", "Code", {
                  required: true,
                  inputProps: { maxLength: 32, style: { textTransform: "uppercase" } },
                  helperText: "Letters, numbers, - or _. Not case sensitive.",
                })}
              </Grid>
              <Grid item xs={12}>{field("description", "Description", { multiline: true, minRows: 2 })}</Grid>
              {!isEdit && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={Boolean(errors.status)}>
                    <InputLabel id="promotion-status-label">Start as</InputLabel>
                    <Select labelId="promotion-status-label" label="Start as" value={form.status} onChange={set("status")}>
                      <MenuItem value="DRAFT">Draft (not usable yet)</MenuItem>
                      <MenuItem value="ACTIVE">Active (usable within its dates)</MenuItem>
                    </Select>
                    {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Section title="Discount" subtitle="Applied once to the registration's membership fee" colors={colors}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={isLocked("promotion_type")} error={Boolean(errors.promotion_type)}>
                  <InputLabel id="promotion-type-label">Promotion type</InputLabel>
                  <Select
                    labelId="promotion-type-label"
                    label="Promotion type"
                    value={form.promotion_type}
                    onChange={set("promotion_type")}
                  >
                    {PROMOTION_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                  {errors.promotion_type && <FormHelperText>{errors.promotion_type}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                {field("discount_value", "Value", {
                  type: "number",
                  disabled: form.promotion_type === "ADMISSION_WAIVER",
                  helperText: valueHelp[form.promotion_type],
                })}
              </Grid>
            </Grid>
          </Section>

          <Section title="Validity" subtitle="Sri Lanka dates, both days included" colors={colors}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {field("valid_from", "Valid from", { type: "date", InputLabelProps: { shrink: true } })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {field("valid_until", "Valid until", {
                  type: "date",
                  InputLabelProps: { shrink: true },
                  helperText: "Leave empty for no end date.",
                })}
              </Grid>
            </Grid>
          </Section>
        </Grid>
      </Grid>

      <Section
        title="Eligibility"
        subtitle="Founding packages are never eligible. Couple and Family promotions apply once to the group total."
        colors={colors}
      >
        {errors.eligibility && <Alert severity="error" sx={{ mb: 1.5 }}>{errors.eligibility}</Alert>}
        <TableContainer sx={{ overflowX: "auto", border: `1px solid ${colors.grey[700]}`, borderRadius: 1 }}>
          <Table size="small" sx={{ minWidth: 520, ...styles.tableSx }}>
            <TableHead>
              <TableRow>
                <TableCell>Package</TableCell>
                {DURATION_OPTIONS.map((d) => (
                  <TableCell key={d.value} align="center">{d.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {ELIGIBILITY_ROWS.map((row) => (
                <TableRow key={row.label}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                  {DURATION_OPTIONS.map((d) => {
                    const target = { ...row, duration_code: d.value };
                    return (
                      <TableCell key={d.value} align="center">
                        <Checkbox
                          checked={form.eligibility.some((item) => sameRow(item, target))}
                          onChange={() => toggleEligibility(row, d.value)}
                          disabled={isLocked("eligibility")}
                          inputProps={{ "aria-label": `${row.label} ${d.label}` }}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Section title="Limits" subtitle="How often the code can be used" colors={colors}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {field("max_redemptions", "Campaign limit", { type: "number", helperText: "Leave empty for no limit." })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {field("max_per_member", "Uses per member", { type: "number", helperText: "Matched by NIC / passport." })}
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(form.new_members_only)}
                      onChange={set("new_members_only")}
                      disabled={isLocked("new_members_only")}
                    />
                  }
                  label="New members only (reject a NIC / passport that is already registered)"
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Section title="Terms and notes" colors={colors}>
            <Grid container spacing={2}>
              <Grid item xs={12}>{field("terms", "Terms shown to members", { multiline: true, minRows: 3 })}</Grid>
              <Grid item xs={12}>{field("admin_notes", "Staff notes", { multiline: true, minRows: 2 })}</Grid>
            </Grid>
          </Section>
        </Grid>
      </Grid>

      <Box display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap">
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{ ...styles.neutralButtonSx, width: { xs: "100%", sm: "auto" } }}
          onClick={() => navigate(backTo)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          sx={{ ...styles.primaryButtonSx, width: { xs: "100%", sm: "auto" } }}
          onClick={submit}
          disabled={saving}
        >
          {saving ? "Saving…" : isEdit ? "Save changes" : "Create promotion"}
        </Button>
      </Box>
    </Box>
  );
};

export default HpcPromotionForm;
